import React from "react";
import _ from "lodash";
import showdown from "showdown";
import { CHAT_OPTIONS, OPTIONS, SCROLL_THRESHOLD } from "./Constants";
import {
  PlayCircleOutlined,
  CameraOutlined,
  RootCrossOutlined,
  CodeOutlined,
  PageFoldPortraitOutlined,
  AddOutlined,
  WorkbookOutlined,
} from "@toddle-edu/ds-icons";
import ACLStore from "lib/aclStore";
import { htmlToText } from "Utils";
import { convertHtmlToText, getAssessmentFieldValue } from "Utils/AIUtils";
import {
  getIsAITutorEnhancementsV2Enabled,
  getIsAITutorModalitiesEnabled,
} from "Utils/ACLStoreUtils";

export const isScrollAtStart = element => {
  if (!element) return false;
  return element.scrollLeft - SCROLL_THRESHOLD <= 0;
};

export const isScrollAtEnd = element => {
  if (!element) return false;
  return (
    element.scrollLeft >=
    element.scrollWidth - element.clientWidth - SCROLL_THRESHOLD
  );
};

export const replaceMathEquation = params => {
  const { value = "", newEquation = "", targetDataId } = params;

  if (!_.trim(value)) return newEquation;

  const regex = new RegExp(
    `<img\\s+[^>]*data-id="${targetDataId}"([^>]*)\\/?>`,
    "g"
  );

  const updatedValue = _.replace(value, regex, newEquation);

  if (updatedValue === value) return `${value}${newEquation}`;

  return updatedValue;
};

// Remove commas between curly braces within LaTeX expressions (e.g., "$$\frac{1},{2}$$" -> "$$\frac{1}{2}$$")
// This fixes LaTeX rendering issues where commas appear between braces
export const removeCommasBetweenBraces = content => {
  if (!content) return content;

  // Process block LaTeX ($$...$$)
  let processedContent = _.replace(
    content,
    /\$\$([\s\S]*?)\$\$/g,
    (match, latexContent) => {
      const cleanedLatex = _.replace(latexContent, /\},\{/g, "}{");
      return `$$${cleanedLatex}$$`;
    }
  );

  // Process inline LaTeX ($...$) - avoiding already processed $$ blocks
  processedContent = _.replace(
    processedContent,
    /(\$\$[\s\S]*?\$\$|\$(?!\$)([\s\S]*?)\$(?!\$))/g,
    (match, fullMatch, latexContent) => {
      // Skip if this is a $$ block (already processed)
      if (_.startsWith(match, "$$")) {
        return match;
      }
      // Process inline LaTeX
      const cleanedLatex = _.replace(latexContent, /\},\{/g, "}{");
      return `$${cleanedLatex}$`;
    }
  );

  return processedContent;
};

export const getTabOptions = params => {
  const { t, criteria, selectedTab, source, isSpeakingOnly = false } = params;

  const _isPlayMode = isPlayMode(selectedTab);

  const playMode = {
    value: `${source}:play_mode`,
    label: t("ai:play_mode"),
    suffix: (
      <PlayCircleOutlined
        size={"xxx-small"}
        variant={_isPlayMode ? "default" : "subtle"}
      />
    ),
    unreadCount: (
      <PlayCircleOutlined
        size={"xxx-small"}
        variant={_isPlayMode ? "default" : "subtle"}
      />
    ),
    isPlayMode: true,
  };

  if (isSpeakingOnly) {
    return [playMode];
  } else {
    return [
      playMode,
      ..._.map(criteria, criterion => {
        const { id, label } = criterion || {};
        return { value: id, label: convertHtmlToText(label) };
      }),
    ];
  }
};

export const getConversationIdByEntitySubType = params => {
  const { assessmentDetails, studentAssignmentData, selectedPrompt } = params;
  let { entitySubType } = params;

  if (!entitySubType) entitySubType = "PREVIEW";

  let conversation = [];
  if (!_.isEmpty(selectedPrompt)) {
    conversation = _.get(selectedPrompt, "aiConversations.edges", []);
  } else if (!_.isEmpty(studentAssignmentData)) {
    conversation = _.get(studentAssignmentData, "aiConversations.edges", []);
  } else {
    conversation = _.get(assessmentDetails, "aiConversation.edges", []);
  }

  const currentConversation = _.find(
    conversation,
    ({ node }) => node?.entitySubType === entitySubType
  );

  return _.get(currentConversation, "node.id");
};

export const getEntitySubType = params => {
  const { selectedPrompt, tabValue, entitySubType } = params;

  if (entitySubType) return entitySubType;

  const { action } = _.get(selectedPrompt, "children.0", {});

  if (isPlayMode(tabValue)) {
    return action ? `${getTabValue(tabValue)}:${action}` : "PREVIEW";
  } else {
    return tabValue;
  }
};

export const parseProficiencyLevelConversation = (
  xmlString,
  reverseOrder = true
) => {
  const messages = [];
  const regex = /<rol>\s*(\w+)\s*<\/rol>\s*<cnt>\s*([\s\S]*?)(?:<\/cnt>|$)/g;

  let match;

  while ((match = regex.exec(xmlString)) !== null) {
    // For complete matches (with closing tags)
    if (match[1] && match[2]) {
      messages.push({
        id: _.size(messages),
        authorType: match[1].trim(),
        content: match[2].trim(),
      });
    }
    // For incomplete matches (without closing tags)
    else if (match[3] && match[4]) {
      messages.push({
        id: _.size(messages),
        authorType: match[3].trim(),
        content: match[4].trim(),
      });
    }
  }

  return reverseOrder ? _.reverse(messages) : messages;
};

export const isUserEngagedWithAI = (messages, offset = 0) => {
  const filteredMessages = _.filter(messages, ({ node }) =>
    _.includes(["user", "assistant"], node?.authorType)
  );

  return _.size(filteredMessages) > offset;
};

export const extractContentAndCode = messageContent => {
  const regex = /```([a-zA-Z]+)?\n([\s\S]*?)```/; // Regex to capture code block
  const match = messageContent.match(regex);

  let code = "";
  let content = messageContent;

  if (match) {
    // Extract the code block content
    code = `\`\`\`${match[1] || ""}\n${_.trim(match[2])}\n\`\`\``; // Rewrap the extracted code
    content = messageContent.replace(match[0], ""); // Remove the code block from content
  }

  return { content: _.trim(content), code: _.trim(code) };
};

const EVALUATION_TAGS = {
  pro: "proficiency",
  stn: "strengths",
  aoi: "areasOfImprovement",
  nes: "nextSteps",
};

export const extractEvaluationAnalysisLevels = input => {
  const result = {};

  _.forEach(_.keys(EVALUATION_TAGS), tag => {
    const tagRegex = new RegExp(
      `<${tag}[^>]*>(.*?)</${tag}>|<${tag}[^>]*>(.*)`,
      "gims"
    );
    const matches = [];
    let match;

    while ((match = tagRegex.exec(input)) !== null) {
      matches.push(match[1] || match[2] || "");
    }

    const firstMatch = _.trim(_.first(matches)) || "";

    if (firstMatch) {
      // Extract title if present
      const titleRegex = /<title[^>]*>(.*?)<\/title>/i;
      const titleMatch = firstMatch.match(titleRegex);

      if (titleMatch && getIsAITutorEnhancementsV2Enabled()) {
        // Store the title
        const headerKey = `${EVALUATION_TAGS[tag]}Header`;
        result[headerKey] = _.trim(titleMatch[1]) || "";

        // Store the content without the title tag
        const contentWithoutTitle = _.trim(firstMatch.replace(titleRegex, ""));
        result[EVALUATION_TAGS[tag]] = contentWithoutTitle || "";
      } else {
        // No title tag found, store as content only
        result[EVALUATION_TAGS[tag]] = firstMatch;
      }
    } else {
      // No content found
      result[EVALUATION_TAGS[tag]] = "";
    }
  });

  return result;
};

export const getCleanedContent = string => {
  if (typeof string !== "string") return string;

  return string
    .replace(/<\/?tdt>/g, "") // Remove <tdt> and </tdt> tags
    .replace(/<\/?anls>/g, "") // Remove <anls> and </anls> tags
    .replace(/<p>\s*<\/p>/g, ""); // Remove empty <p> tags
};

export const checkIsSTTLanguageEnabled = () => {
  return ACLStore.can("FeatureFlag:EnableSTTLanguageInput");
};

export const isMessageFlagged = item => {
  if (_.isArray(item)) {
    const flagData = _.find(item, node => {
      return _.get(node, "function.name") === "flag_message";
    });

    if (!flagData) return false;

    const args = _.get(flagData, "function.arguments", {}) || {};
    const { flagged, category } = args;

    return Boolean(flagged) && category !== "Miscellaneous";
  }

  const category = _.get(item, "node.metadata.category");
  return Boolean(category) && category !== "Miscellaneous";
};

/**
 * Decodes HTML entities in a string
 * @param {string} str - String with HTML entities
 * @returns {string} - Decoded string
 */
export const decodeHTMLEntities = str => {
  if (!str || typeof str !== "string") return str;
  return _.unescape(str);
};

export const INCOMPLETE_OUTPUT_TAG_AT_END = /(?:\n|\r|\s)*<output\s*$/i;

export const extractStateAndContent = inputText => {
  // Regex to match valid tags (`reasoning`, `thinking`, `transcript`, `instructions`, or `output`) and their content
  const regex = /<(reasoning|thinking|transcript|instructions|output)>\s*([\s\S]*?)(?:<\/\1>|$)/g;

  const result = {
    reasoning: null,
    output: null,
    content: null,
  };

  if (!inputText) return result;

  let match;
  let strippedText = inputText;
  let lastMatchEnd = 0;

  while ((match = regex.exec(inputText)) !== null) {
    const state = _.nth(match, 1);
    let content = _.nth(match, 2);

    // If this is an output tag, clean nested tags
    if (state === "output") {
      // Remove reasoning, thinking, instructions, transcript  tags AND their content from inside output
      content = _.replace(content, /<reasoning>[\s\S]*?<\/reasoning>/g, "");
      content = _.replace(content, /<thinking>[\s\S]*?<\/thinking>/g, "");

      content = _.replace(content, /<transcript>[\s\S]*?<\/transcript>/g, "");
      content = _.replace(
        content,
        /<instructions>[\s\S]*?<\/instructions>/g,
        ""
      );

      // Remove nested output tags but keep their content
      content = _.replace(content, /<\/?output>/g, "");
    }

    // Treat 'thinking' as an alias for 'reasoning'
    const normalizedState = state === "thinking" ? "reasoning" : state;
    result[normalizedState] = _.trim(decodeHTMLEntities(content));

    // Track the end position of the last matched tag block
    lastMatchEnd = regex.lastIndex;

    // Remove this tagged block from the remaining text
    strippedText = strippedText.replace(match[0], "");
  }

  strippedText = strippedText.replace(INCOMPLETE_OUTPUT_TAG_AT_END, "");

  // If reasoning exists but output doesn't, keep remaining text as content
  if (result.reasoning && !result.output) {
    const textAfterTags = inputText.substring(lastMatchEnd);
    result.content = _.trim(decodeHTMLEntities(textAfterTags));
  }

  // No tags at all
  if (
    !result.reasoning &&
    !result.output &&
    !_.startsWith(inputText, "<rea") &&
    !_.startsWith(inputText, "<thi")
  ) {
    result.content = _.trim(decodeHTMLEntities(inputText));
  }

  return result;
};

export const checkIsThinkingState = messages => {
  if (_.isEmpty(messages)) return false;
  const lastMessageContent = _.get(_.last(messages), "node.content");

  const { content, output } = extractStateAndContent(lastMessageContent);
  return !(content || output);
};

export const getAssessmentRequiredFieldValues = _.memoize(
  params => {
    const { fields, assessmentDetails } = params;

    return _.compact(
      _.map(fields, field => {
        return {
          [field]: getAssessmentFieldValue({
            fields: assessmentDetails?.allFields,
            uid: field,
          }),
        };
      })
    );
  },
  params => {
    const { fields, assessmentDetails } = params;
    const fieldValues = _.map(fields, field => {
      return {
        [field]: getAssessmentFieldValue({
          fields: assessmentDetails?.allFields,
          uid: field,
        }),
      };
    });

    return JSON.stringify({ fieldValues });
  }
);

export const getTabValue = tabValue => {
  return _.last(_.split(tabValue, ":"));
};

export const isPlayMode = tabValue => {
  return _.includes(tabValue, "play_mode");
};

export const convertMarkdownToHTML = ({
  content,
  shouldParseContent = false,
}) => {
  const converter = new showdown.Converter({
    tables: true,
    // disableForced4SpacesIndentedSublists is used to disable the 4 spaces indentation for lists
    // setting this to true allows us to use 2 spaces indentation for lists
    disableForced4SpacesIndentedSublists: true,
  });

  if (shouldParseContent) {
    const parsedContent = _.replace(content, /###/g, "@@@")?.trim();
    const covertedText = converter.makeHtml(parsedContent);

    return _.replace(covertedText, /@@@/g, "###");
  }

  return converter.makeHtml(content);
};

// for RTE, we use heading from level 3 to 5
// so we need to replace h1 with h3, h2 with h4 and h3 with h5
const replaceHTMLHeadings = htmlString => {
  if (!htmlString) return htmlString;

  const mapping = { 1: "3", 2: "4", 3: "5" };
  // Match both opening and closing tags of h1, h2, h3
  return htmlString.replace(
    /<(\/?)h([1-3])([^>]*)>/gi,
    (match, slash, level, rest) => {
      const newLevel = mapping[level];
      return `<${slash}h${newLevel}${rest}>`;
    }
  );
};

export const getTextForClipboard = ({ content }) => {
  const htmlText = convertMarkdownToHTML({ content });

  const plainText = htmlToText(htmlText, {
    wordwrap: null,
  })?.trim();

  return { htmlText: replaceHTMLHeadings(htmlText), plainText };
};

const getChatOptionsV2 = hasOpeningMsgAttachment => {
  const options = [
    {
      id: "add_attachment",
      key: OPTIONS.ADD_ATTACHMENT_FROM_DEVICE,
      icon: <AddOutlined />,
      tooltip: "common:attach_files",
      showTooltip: true,
    },
    {
      id: "add_image",
      key: OPTIONS.ADD_IMAGE_FROM_CAMERA,
      icon: <CameraOutlined />,
      tooltip: "common:take_photo",
      showTooltip: true,
    },
    {
      id: "add_equation",
      key: OPTIONS.ADD_MATH_EQUATION,
      icon: <RootCrossOutlined />,
      tooltip: "equationEditor:equation_editor_heading",
      showTooltip: true,
    },
  ];

  if (!hasOpeningMsgAttachment) {
    options.push(
      {
        id: "code_editor",
        key: OPTIONS.CODE_EDITOR,
        icon: <CodeOutlined />,
        tooltip: "ai:code_editor",
        showTooltip: true,
      },
      {
        id: "document",
        key: OPTIONS.TODDLE_DOC,
        icon: <PageFoldPortraitOutlined />,
        tooltip: "common:document",
        showTooltip: true,
      },
      {
        id: "workbook",
        key: OPTIONS.WORKBOOK,
        icon: <WorkbookOutlined />,
        tooltip: "ai:whiteboard",
        showTooltip: true,
      }
    );
  }

  return options;
};

export const getAvailableChatOptions = (
  assessmentDetails,
  hasOpeningMsgAttachment
) => {
  const allFields = _.get(assessmentDetails, "allFields", []);

  const studentCommunicationMode = getAssessmentFieldValue({
    fields: allFields,
    uid: "studentCommunicationMode",
  });

  const isSpeakingOnly = studentCommunicationMode === "speaking_only";
  const isWritingOnly = studentCommunicationMode === "writing_only";

  const chatOptions = getIsAITutorModalitiesEnabled()
    ? getChatOptionsV2(hasOpeningMsgAttachment)
    : CHAT_OPTIONS;

  if (isSpeakingOnly) {
    return [];
  } else if (isWritingOnly) {
    return _.filter(chatOptions, ({ key }) => key !== OPTIONS.ADD_AUDIO);
  }

  return chatOptions;
};
